/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * ComponentEditor — picks which reusable component this section includes.
 *
 * The section stores only a reference:
 *
 *     section_config = { component_ref: "main-header" }
 *
 * and the server swaps it for the component's sections when the page is served.
 * That indirection is the whole point: because nothing is copied in here, an
 * edit to the component reaches every page that includes it. Adding a button to
 * a shared header becomes one edit rather than one edit per page.
 *
 * The reference is the component's SLUG, never its row number, so a page that
 * is exported and seeded into another application still points at the right
 * thing — provided a component with that slug exists there.
 */

import React from 'react';
import { usePages } from '../../../hooks/usePages';
import EditorField from './_EditorField';

export default function ComponentEditor({ section, dispatch, appId }) {
  const cfg = section.section_config || {};
  const { data, isLoading } = usePages(appId);

  const all = data?.pages || data || [];
  const components = all.filter(p => p.page_type === 'component');
  const selected = components.find(c => c.slug === cfg.component_ref);

  const update = patch =>
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });

  return (
    <div>
      <EditorField
        label="Component"
        hint="Edit the component itself to change it everywhere it is used."
      >
        {isLoading ? (
          <div className="text-muted small">
            <span className="spinner-border spinner-border-sm me-1" />
            Loading components…
          </div>
        ) : (
          <select
            className="form-select form-select-sm"
            value={cfg.component_ref || ''}
            onChange={e => update({ component_ref: e.target.value })}
          >
            <option value="">Select a component…</option>
            {components.map(c => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
        )}
      </EditorField>

      {!isLoading && components.length === 0 && (
        <div className="alert alert-warning py-2 small mb-0">
          <i className="bi bi-exclamation-triangle me-1" />
          This application has no components yet. Create one from the Page Builder
          hub, then come back and select it here.
        </div>
      )}

      {!isLoading && cfg.component_ref && !selected && (
        // The component was renamed or deleted after this section was added.
        // The page still renders — this section simply contributes nothing —
        // so the warning has to be here, where it can still be fixed.
        <div className="alert alert-danger py-2 small mb-0">
          <i className="bi bi-exclamation-octagon me-1" />
          No component named <code>{cfg.component_ref}</code> exists in this
          application. This section will render nothing until you pick one that does.
        </div>
      )}

      {selected && (
        <div className="text-muted small mt-2">
          Included by reference as <code>{selected.slug}</code>.
        </div>
      )}
    </div>
  );
}
